import React, { ReactNode, } from "react";

export type SkeletonAnimation = "pulse" | "wave" | false;
export type SkeletonVariant = "text" | "rect" | "circle";

export type SkeletonProps = React.HTMLAttributes<HTMLDivElement> & {
  animation?: SkeletonAnimation;
  children?: ReactNode;
  height?: number | string;
  variant?: SkeletonVariant;
  width?: number | string;
};

export default function Skeleton({
  animation = "pulse",
  children,
  height,
  variant = "text",
  width,
  className = "",
  style,
  ...props
}: SkeletonProps): JSX.Element {
  const hasChildren = Boolean(children);

  const getVariantClasses = (): string => {
    if (variant === "text") {
      // O text utiliza a altura da linha nativa se vazio, injetando um espaço em branco invisível
      return "rounded-md empty:before:content-[\"\\00a0\"]";
    }
    if (variant === "circle") {
      return "rounded-full";
    }
    if (variant === "rect") {
      return "rounded-sm";
    }

    return "";
  };

  const getAnimationClasses = (): string => {
    if (animation === "pulse") return "animate-pulse";
    if (animation === "wave") return "relative overflow-hidden";

    return "";
  };

  return (
    <div
      className={`bg-gray-200 dark:bg-gray-700 ${getVariantClasses()} ${getAnimationClasses()} ${
        hasChildren ? "w-fit h-fit [&>*]:invisible" : "block"
      } ${className}`}
      style={{ width, height, ...style }}
      {...props}
    >
      {animation === "wave" && (
        <>
          <style>{`
            @keyframes mui-skeleton-wave {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            .animate-skeleton-wave {
              animation: mui-skeleton-wave 1.6s linear 0.5s infinite;
            }
          `}</style>
          {/* Elemento absoluto que cria o efeito de brilho passando por cima do skeleton */}
          <div className="absolute inset-0 -translate-x-full animate-skeleton-wave bg-gradient-to-r from-transparent via-white/50 to-transparent dark:via-gray-500/50" />
        </>
      )}

      {/* Os children ficam invisíveis, mas garantem que o skeleton assuma o tamanho exato deles */}
      {children}
    </div>
  );
}